import { adminRequest, getAdminToken } from './adminApi'
import { API_BASE, ApiError } from './http'
import { downloadBlob } from '@/lib/download'

export interface AdminBackupMetadata {
  created_at: string
  nf_version: string
  schema_migration_version: number
  integrity_status: string
  db_size: number
  db_sha256: string
  user_count: number
  project_count: number
  label: string
}

export interface AdminBackupFile {
  name: string
  path: string
  timestamp: number
  size: number
  sha256: string
  metadata: AdminBackupMetadata
}

export interface AdminIntegrityStatus {
  known: boolean
  ok: boolean
  result: string
  checked_at: number
}

export interface ListAdminBackupsResponse {
  backups: AdminBackupFile[]
  last_integrity: AdminIntegrityStatus
}

export interface RunAdminBackupResponse {
  backup: AdminBackupFile
}

export type BackupType = 'core' | 'assets'
export type AdminBackupTarget = 'local' | 's3'

export function normalizeAdminBackupTarget(target: string): AdminBackupTarget {
  const normalized = target.trim().toLowerCase()
  return normalized === 's3' || normalized === 'remote' ? 's3' : 'local'
}

export interface AdminBackupTypeConfig {
  type: BackupType
  enabled: boolean
  interval: string
  keep_n: number
  target: string
}

export interface AdminBackupTypeConfigOverride {
  type: BackupType
  enabled?: boolean
  interval?: string
  keep_n?: number
  target?: string
  updated_at?: number
  updated_by?: string
}

export interface AdminBackupConfigTypeResponse {
  baseline: AdminBackupTypeConfig
  override: AdminBackupTypeConfigOverride
  effective: AdminBackupTypeConfig
}

export interface AdminBackupRemoteStatus {
  state: string
  consecutive_failure: number
  last_failure: string
}

export interface AdminBackupConfigResponse {
  configs: {
    core: AdminBackupConfigTypeResponse
    assets: AdminBackupConfigTypeResponse
  }
  remote: Record<BackupType, AdminBackupRemoteStatus>
  passphrase_set: boolean
}

export interface AdminBackupEncStatusResponse {
  passphrase_set: boolean
  last_encryption: string
}

export interface RestoreAdminBackupResponse {
  staged: boolean
  prerestore_backup: string
  restart_required: boolean
  allow_self_restart: boolean
}

export interface AdminBackupTypePayload {
  enabled?: boolean
  interval?: string
  keep_n?: number
  target?: AdminBackupTarget
}

export function runAdminBackup(type: BackupType = 'core', label?: string): Promise<RunAdminBackupResponse> {
  const qs = new URLSearchParams({ type })
  return adminRequest<RunAdminBackupResponse>(`/v1/admin/backup?${qs.toString()}`, {
    method: 'POST',
    body: label ? { label } : undefined,
  })
}

export function manualRunBackup(type: BackupType, label?: string): Promise<RunAdminBackupResponse> {
  return runAdminBackup(type, label)
}

export function listAdminBackups(type?: BackupType): Promise<ListAdminBackupsResponse> {
  const qs = type ? `?type=${encodeURIComponent(type)}` : ''
  return adminRequest<ListAdminBackupsResponse>(`/v1/admin/backups${qs}`)
}

export function getBackupConfig(): Promise<AdminBackupConfigResponse> {
  return adminRequest<AdminBackupConfigResponse>('/v1/admin/backup/config')
}

export function putBackupConfig(type: BackupType, payload: AdminBackupTypePayload): Promise<void> {
  const body = type === 'core' ? { core: payload } : { assets: payload }
  return adminRequest<void>('/v1/admin/backup/config', {
    method: 'PUT',
    body,
  })
}

export function setBackupPassphrase(passphrase: string): Promise<void> {
  return adminRequest<void>('/v1/admin/backup/passphrase', {
    method: 'PUT',
    body: { passphrase },
  })
}

export function getEncStatus(): Promise<AdminBackupEncStatusResponse> {
  return adminRequest<AdminBackupEncStatusResponse>('/v1/admin/backup/enc-status')
}

export function deleteAdminBackup(name: string): Promise<void> {
  return adminRequest<void>(`/v1/admin/backups/${encodeURIComponent(name)}`, { method: 'DELETE' })
}

export function restoreAdminBackup(name: string, confirm: string, type?: BackupType): Promise<RestoreAdminBackupResponse> {
  const qs = type ? `?type=${encodeURIComponent(type)}` : ''
  return adminRequest<RestoreAdminBackupResponse>(`/v1/admin/restore${qs}`, {
    method: 'POST',
    body: { backup_name: name, confirm },
  })
}

export async function uploadAdminRestore(file: File, confirm: string): Promise<RestoreAdminBackupResponse> {
  const token = getAdminToken()
  if (!token) throw new Error('admin token missing')
  const body = new FormData()
  body.set('confirm', confirm)
  body.set('file', file)
  const res = await fetch(`${API_BASE}/v1/admin/restore`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
    body,
  })
  const data = await res.json().catch(() => undefined)
  if (!res.ok) {
    const message = data?.error?.message ?? data?.message ?? res.statusText
    const code = data?.error?.code ?? data?.code
    throw new ApiError(res.status, message, code)
  }
  return data as RestoreAdminBackupResponse
}

export async function downloadAdminBackup(name: string): Promise<void> {
  const token = getAdminToken()
  if (!token) throw new Error('admin token missing')
  const res = await fetch(`${API_BASE}/v1/admin/backups/${encodeURIComponent(name)}/download`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
  })
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText)
  }
  const blob = await res.blob()
  downloadBlob(blob, name)
}

export function restartAdminServer(): Promise<{ restarting: boolean }> {
  return adminRequest<{ restarting: boolean }>('/v1/admin/restart', { method: 'POST' })
}
